import { type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { Button, Field, Notice, Select, TextArea, Row, FormStack } from '@vello/web-theme';
import { OPS_CANCEL_NOTE_MAX_LENGTH, OPS_CANCEL_REASON_CODES, validateRideCancelDraft, type OpsCancelInput } from '../../src/index';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { FormDialog } from './form-dialog';

const REASON_ID = 'ride-cancel-reason';
const NOTE_ID = 'ride-cancel-note';

export interface RideCancelDialogProps {
  readonly open: boolean;
  /** The ride's human code (or id) named in the title. */
  readonly rideLabel: string | undefined;
  /** A cancel is in flight: the controls are held and the scrim/Escape do not close. */
  readonly busy: boolean;
  /** The translated refusal of the last attempt; the dialog stays open to correct or retry. */
  readonly error: string | undefined;
  readonly onCancel: () => void;
  readonly onConfirm: (input: OpsCancelInput) => void;
}

/**
 * The support cancel dialog (W5-HOTFIX, D382) — on the shared {@link FormDialog} shell (the design system's own layer and
 * modal behaviour) like the courier release dialog (D378), instead of the shared `ConfirmDialog`, whose chips and
 * buttons miss the 48 px floor and whose note field has no visible label. Every control is a theme primitive at
 * `--tap-target-min`: the reason `Select`, the note `TextArea`, the two `Button`s.
 *
 * What it guarantees: the reason is a code from the server's closed vocabulary (the only thing the rider, the driver and
 * the partner desk ever read); the optional note is labelled — and its hint says — INTERNAL, and it is sent as its own
 * field, which the server writes to the operator audit only. One primary action (the confirm), disabled until a reason
 * is picked and the note fits. Focus lands on the reason when it opens, Tab stays inside, Escape and the scrim cancel,
 * and focus returns to the control that opened it (all of it the {@link FormDialog} shell's).
 */
export function RideCancelDialog({ open, rideLabel, busy, error, onCancel, onConfirm }: RideCancelDialogProps): ReactElement | null {
  const { t } = useT();
  const { v } = useVocab();
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const cancel = (): void => {
    if (!busy) onCancel();
  };

  // A fresh draft per opening: no reason picked, no note.
  useEffect(() => {
    if (!open) return;
    setCode('');
    setNote('');
  }, [open, rideLabel]);

  if (rideLabel === undefined) return null;

  const draft = validateRideCancelDraft({ code, note });
  const tooLong = !draft.ok && draft.issue === 'NOTE_TOO_LONG';
  const unstorable = !draft.ok && draft.issue === 'NOTE_INVALID';

  const submit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (busy || !draft.ok) return;
    onConfirm(draft.input);
  };

  return (
    <FormDialog open={open} id="ride-cancel" icon="ban" tone="danger" title={t('ride.cancel.title', { code: rideLabel })} body={t('ride.cancel.body')} busy={busy} initialFocusId={REASON_ID} onCancel={onCancel}>
      <FormStack gap={4} onSubmit={submit} noValidate>
        <Field label={t('ride.cancel.reasonLabel')} htmlFor={REASON_ID}>
          <Select id={REASON_ID} value={code} onChange={(e) => setCode(e.target.value)} required aria-required="true" disabled={busy}>
            <option value="">{t('ride.cancel.reason.choose')}</option>
            {OPS_CANCEL_REASON_CODES.map((c) => (
              <option key={c} value={c}>{v('ops.cancel_reason', c)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('ride.cancel.note')} htmlFor={NOTE_ID} hint={t('ride.cancel.note.hint', { max: OPS_CANCEL_NOTE_MAX_LENGTH })}>
          <TextArea
            id={NOTE_ID}
            value={note}
            maxLength={OPS_CANCEL_NOTE_MAX_LENGTH}
            onChange={(e) => setNote(e.target.value)}
            aria-invalid={tooLong || unstorable}
            disabled={busy}
            rows={3}
          />
        </Field>
        {tooLong && (
          <div>
            <Notice tone="warning">{t('ride.cancel.note.tooLong', { max: OPS_CANCEL_NOTE_MAX_LENGTH })}</Notice>
          </div>
        )}
        {unstorable && (
          <div>
            <Notice tone="warning">{t('ride.cancel.note.invalid')}</Notice>
          </div>
        )}
        {error !== undefined && (
          <div>
            <Notice tone="danger">{error}</Notice>
          </div>
        )}
        <Row gap={2} align="stretch">
          <Button className="vello-admin-a-btn-grow" variant="ghost" onClick={cancel} disabled={busy}>
            {t('ride.cancel.keep')}
          </Button>
          <Button className="vello-admin-a-btn-grow" type="submit" variant="danger" disabled={busy || !draft.ok}>
            {busy ? t('ride.cancel.sending') : t('ride.cancel.action')}
          </Button>
        </Row>
      </FormStack>
    </FormDialog>
  );
}
